// Report pages keep their view (filters, search text) in the query string, which can hold personal data.
// Error tracking only needs the path, so everything from the "?" or "#" of a /reports URL to the end of that
// string is dropped from what Sentry sends. Inputs are never mutated: they are the app's own objects (console
// arguments, application state, frozen objects), so a scrubbed deep copy is returned instead.
const MAX_JSON = 512 * 1024;
// A /reports URL (the reports route is not exact: /reports/anything renders the page too) followed by a query or
// hash loses everything up to the end of the JSON string holding it. A single forward scan, so it stays linear for
// any input and has no length limit on the subpath. Over-scrubbing the rest of that string is intended.
const STOP = '?#"\\';
const scrubJsonText = (json) => {
  const lower = json.toLowerCase();
  let out = '';
  let from = 0;
  let at = lower.indexOf('/reports');
  while (at !== -1) {
    let end = at + 8; // just after "/reports"
    if (json[end] === '/') {
      while (end < json.length && !STOP.includes(json[end])) end += 1; // optional subpath
    }
    if (json[end] === '?' || json[end] === '#') {
      while (end < json.length && json[end] !== '"') end += json[end] === '\\' ? 2 : 1; // to the end of the JSON string
      out += `${json.slice(from, at)}/reports`;
      from = end;
    }
    at = lower.indexOf('/reports', Math.max(end, at + 1));
  }
  return out + json.slice(from);
};
const FAIL = Symbol('scrub-failed');

const isNode = value => typeof value.nodeType === 'number' && typeof value.nodeName === 'string';

const safeReplacer = () => {
  const ancestors = [];
  // eslint-disable-next-line func-names
  return function (key, value) {
    if (typeof value === 'function') return undefined;
    if (!value || typeof value !== 'object') return value;
    if (isNode(value)) return '[Node]';
    // Errors have no own enumerable fields and would serialise to {}; keep what helps debugging.
    if (value instanceof Error) value = {name: String(value.name), message: String(value.message), stack: String(value.stack)};
    // `this` is the object holding `key`: drop the ancestors that are already finished.
    while (ancestors.length && ancestors[ancestors.length - 1] !== this) ancestors.pop();
    if (ancestors.includes(value)) return '[Circular]';
    ancestors.push(value);
    return value;
  };
};

// Deep, scrubbed copy of `value`, or FAIL when it cannot be done safely (too big, throwing getters, BigInt...).
const scrubCopy = (value) => {
  try {
    const json = JSON.stringify(value, safeReplacer());
    if (json === undefined || json.length > MAX_JSON) return FAIL;
    return JSON.parse(scrubJsonText(json));
  } catch (e) {
    return FAIL;
  }
};

export const scrubBreadcrumb = (breadcrumb) => {
  const copy = scrubCopy(breadcrumb);
  return copy === FAIL ? null : copy;
};

// Transactions skip beforeSend; this runs as a global event processor. A transaction that cannot be scrubbed is dropped.
export const scrubTransaction = (event) => {
  const copy = scrubCopy(event);
  return copy === FAIL ? null : copy;
};

const origin = (url) => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (e) {
    return String(url).replace(/[?#].*$/, '');
  }
};

// What is left of an error event when it cannot be copied whole: the exception and its stack, and nothing
// that can hold application state (breadcrumbs, extra, contexts).
const minimalEvent = (event) => {
  const minimal = {};
  ['event_id', 'timestamp', 'level', 'platform', 'release', 'environment', 'message', 'transaction'].forEach((key) => {
    if (typeof event[key] === 'string' || typeof event[key] === 'number') minimal[key] = event[key];
  });
  if (event.request && event.request.url) minimal.request = {url: origin(event.request.url)};
  if (event.exception && Array.isArray(event.exception.values)) {
    minimal.exception = {
      values: event.exception.values.map(exception => ({
        type: exception.type,
        value: exception.value,
        mechanism: exception.mechanism,
        stacktrace: exception.stacktrace && Array.isArray(exception.stacktrace.frames)
          ? {frames: exception.stacktrace.frames.map(({filename, abs_path: absPath, function: fn, lineno, colno, module, in_app: inApp}) =>
            ({filename, abs_path: absPath, function: fn, lineno, colno, module, in_app: inApp}))}
          : undefined
      }))
    };
  }
  return minimal;
};

export const scrubEvent = (event) => {
  const copy = scrubCopy(event);
  if (copy !== FAIL) {
    if (copy && copy.request) delete copy.request.query_string;
    return copy;
  }
  try {
    const minimal = scrubCopy(minimalEvent(event));
    return minimal === FAIL ? null : minimal;
  } catch (e) {
    return null;
  }
};
